import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw, Check } from "lucide-react";
import type { LocalFile } from "@/components/file-upload/local-file-input";

const CANVAS_WIDTH = 480;
const CANVAS_HEIGHT = 160;

interface SignatureCanvasProps {
  /** Called when the user accepts the drawn signature. */
  onCapture: (file: LocalFile) => void;
  /** Called when the captured signature is cleared. */
  onClear: () => void;
  /** Whether a signature has already been captured (shows a confirmation state). */
  hasCaptured: boolean;
  disabled?: boolean;
}

/**
 * A small draw-to-sign canvas.
 *
 * - The user draws a signature with mouse or touch.
 * - On "Accept", the canvas is exported as a PNG `File` and returned via
 *   `onCapture` as a `LocalFile`, which the parent can pass through the
 *   existing TUS file-upload path (same as FileUpload fields).
 * - The component is stateless with respect to the captured file; the parent
 *   controls whether to show the captured state via `hasCaptured`.
 */
export function SignatureCanvas({
  onCapture,
  onClear,
  hasCaptured,
  disabled = false,
}: SignatureCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // ── Canvas helpers ──────────────────────────────────────────────────────

  const getCtx = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return null;
    }
    return { canvas, ctx };
  }, []);

  const clearCanvas = useCallback(() => {
    const result = getCtx();
    if (!result) {
      return;
    }
    result.ctx.clearRect(0, 0, result.canvas.width, result.canvas.height);
    setHasDrawn(false);
  }, [getCtx]);

  const getPos = (
    e: React.MouseEvent | React.TouchEvent,
    canvas: HTMLCanvasElement,
  ) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ("touches" in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  // ── Draw handlers ───────────────────────────────────────────────────────

  const startDraw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled || hasCaptured) {
        return;
      }
      const result = getCtx();
      if (!result) {
        return;
      }
      e.preventDefault();
      isDrawingRef.current = true;
      const pos = getPos(e, result.canvas);
      result.ctx.beginPath();
      result.ctx.moveTo(pos.x, pos.y);
    },
    [disabled, hasCaptured, getCtx],
  );

  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawingRef.current || disabled || hasCaptured) {
        return;
      }
      const result = getCtx();
      if (!result) {
        return;
      }
      e.preventDefault();
      const pos = getPos(e, result.canvas);
      result.ctx.lineTo(pos.x, pos.y);
      result.ctx.stroke();
      setHasDrawn(true);
    },
    [disabled, hasCaptured, getCtx],
  );

  const stopDraw = useCallback(() => {
    isDrawingRef.current = false;
  }, []);

  // ── Canvas setup ────────────────────────────────────────────────────────

  useEffect(() => {
    const result = getCtx();
    if (!result) {
      return;
    }
    const { ctx } = result;
    ctx.strokeStyle = "#1e3a5f";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [getCtx]);

  // ── Accept handler ──────────────────────────────────────────────────────

  const handleAccept = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }
      const file = new File([blob], "signature.png", { type: "image/png" });
      const localFile: LocalFile = {
        id: crypto.randomUUID(),
        file,
      };
      onCapture(localFile);
    }, "image/png");
  }, [onCapture]);

  const handleClear = useCallback(() => {
    clearCanvas();
    onClear();
  }, [clearCanvas, onClear]);

  // ── Render ──────────────────────────────────────────────────────────────

  if (hasCaptured) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 shrink-0">
          <Check size={16} className="text-green-600" />
        </div>
        <p className="flex-1 text-sm text-green-800 font-medium">
          Signature captured
        </p>
        {!disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 shrink-0"
            onClick={handleClear}
          >
            <RotateCcw size={13} className="mr-1" />
            Redo
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        className={`relative rounded-lg border-2 ${
          disabled ? "border-gray-100 bg-gray-50" : "border-gray-200 bg-white"
        } overflow-hidden`}
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="w-full block touch-none"
          style={{ cursor: disabled ? "default" : "crosshair" }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
        {!hasDrawn && !disabled && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <p className="text-sm text-gray-400 select-none">
              Draw your signature here
            </p>
          </div>
        )}
        {/* Baseline guide */}
        <div className="pointer-events-none absolute bottom-8 left-6 right-6 border-b border-dashed border-gray-200" />
      </div>
      <div className="flex justify-between items-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 text-xs"
          onClick={handleClear}
          disabled={!hasDrawn || disabled}
        >
          <RotateCcw size={12} className="mr-1" />
          Clear
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-7 px-3 text-xs"
          onClick={handleAccept}
          disabled={!hasDrawn || disabled}
        >
          <Check size={12} className="mr-1" />
          Accept Signature
        </Button>
      </div>
    </div>
  );
}
